// src/store/slices/authSlice.ts

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Role is dynamic now (see types/rbac.ts::RoleEntry) — `role` here is just the
// denormalized display name returned by the server ('Admin'/'PA' included).
export interface User {
  _id:                string;
  email:              string;
  name:               string;
  role:               string;
  roleId?:            string;
  department?:        string;
  isAdmin?:           boolean;
  mustResetPassword?: boolean;
  avatar?:            string;
  lastLogin?:         string;
  mobile?:            string;
  isEmailVerified?:   boolean;
  isMobileVerified?:  boolean;
}

interface AuthState {
  user:            User | null;
  accessToken:     string | null;
  isAuthenticated: boolean;
  isInitializing:  boolean;
  // How many seconds until access token expires (for UI countdown if needed)
  tokenExpiresAt:  number | null;   // Unix timestamp ms
}

const initialState: AuthState = {
  user:            null,
  accessToken:     null,
  isAuthenticated: false,
  isInitializing:  true,
  tokenExpiresAt:  null,
};

function decodeExpiry(token: string): number | null {
  try {
    const [, b64] = token.split('.');
    const { exp } = JSON.parse(atob(b64));
    return exp * 1000;   // convert to ms
  } catch {
    return null;
  }
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Full credentials on login or successful session restore
    setCredentials: (state, action: PayloadAction<{ user: User; accessToken: string }>) => {
      state.user            = action.payload.user;
      state.accessToken     = action.payload.accessToken;
      state.isAuthenticated = true;
      state.isInitializing  = false;
      state.tokenExpiresAt  = decodeExpiry(action.payload.accessToken);
    },

    // Access token rotated (refresh) — keep user, update token
    setToken: (state, action: PayloadAction<string>) => {
      state.accessToken    = action.payload;
      state.tokenExpiresAt = decodeExpiry(action.payload);
    },

    // Partial profile edit (name/avatar/email/mobile change) — merges into
    // the current user without touching tokens.
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) state.user = { ...state.user, ...action.payload };
    },

    // Silent refresh attempt finished but user is not authenticated
    setInitialized: (state) => {
      state.isInitializing = false;
    },

    // Full logout — clear everything
    logout: (state) => {
      state.user            = null;
      state.accessToken     = null;
      state.isAuthenticated = false;
      state.isInitializing  = false;
      state.tokenExpiresAt  = null;
    },
  },
});

export const { setCredentials, setToken, setInitialized, logout, updateUser } = authSlice.actions;
export default authSlice.reducer;
